// API Client Service - Real backend integration
// Replaces all mock data with actual API calls to Express backend

import { getRuntimeConfig } from '../config/runtimeConfig';
import { ServiceError } from './ServiceError';

// CRITICAL: API_BASE_URL must include /api suffix
// Backend routes: /api/auth, /api/domains, /api/policies, etc.
// DO NOT remove /api from the URL or authentication will break
const getApiBaseUrl = () => {
  try {
    return getRuntimeConfig().apiUrl;
  } catch {
    return ''; // Will fail safely if config not loaded
  }
};

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  metadata?: {
    timestamp: string;
    requestId: string;
  };
  // Allow additional fields from backend
  [key: string]: any;
}

interface ApiError {
  error: {
    code: string;
    message: string;
    details?: any;
    timestamp: string;
    requestId: string;
    retryable?: boolean;
  };
}

class ApiClient {
  private baseUrl: string;
  private getAuthToken: (() => Promise<string | null>) | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || getApiBaseUrl();
  }

  /**
   * Set the authentication token getter function
   * This function is called by MSALAuthContext and returns the current Azure AD access token
   * The token is acquired via MSAL's acquireTokenSilent method
   * @param getToken - Async function that returns the current access token or null
   */
  setAuthTokenGetter(getToken: () => Promise<string | null>) {
    this.getAuthToken = getToken;
  }

  /**
   * Update the base URL for the API client
   * This is called when runtime configuration is loaded
   * @param baseUrl - The new base URL to use for API requests
   */
  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
    console.log('🔧 API Client base URL updated to:', baseUrl);
  }

  // CRITICAL FIX: Handle different API URL formats between environments
  //
  // Problem: Environment variables configure base URLs differently:
  // - Localhost (.env.local): REACT_APP_API_URL = "http://localhost:3000/api"
  // - Azure (GitHub Actions): REACT_APP_API_URL = "https://lseg-teams-express...io"
  //
  // This caused API routing failures:
  // - Localhost without fix: http://localhost:3000/api/api/policies ❌ DOUBLE /api
  // - Azure without fix: https://lseg-teams-express...io/policies ❌ MISSING /api
  //
  // Solution: Detect base URL format and normalize endpoints accordingly
  private normalizeEndpoint(endpoint: string): string {
    let normalizedEndpoint: string;

    if (this.baseUrl.endsWith('/api')) {
      endpoint = endpoint.replace(/^\/api/, '');
      // Base URL already includes /api (localhost development)
      // Just append the endpoint: /api + /policies = /api/policies
      normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    } else {
      // Base URL doesn't include /api (Azure production)
      // Add /api prefix: baseUrl + /api + /policies = baseUrl/api/policies
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint :
        endpoint.startsWith('/') ? `/api${endpoint}` :
          `/api/${endpoint}`;
    }

    return normalizedEndpoint;
  }

  // CRITICAL SECURITY: Authentication token handling
  // ALWAYS require real authentication tokens - no bypasses allowed
  private async prepareAuthHeaders(endpoint: string, options: RequestInit): Promise<Record<string, string>> {
    // Get authentication token dynamically (await the async getter)
    console.log(`🔐 Requesting token for ${options.method || 'GET'} ${endpoint}`);
    const token = await this.getAuthToken?.();

    const authHeaders: Record<string, string> = {};

    if (!token) {
      // No token available - user must authenticate
      // This forces proper authentication flow via MSAL
      console.error(`🔒 No authentication token available for ${options.method || 'GET'} ${endpoint} - user must sign in`);
      console.log('🔑 Token getter function exists:', !!this.getAuthToken);
    } else {
      // Normal path: Use real authentication token from Azure AD
      // This token is obtained via MSAL and validated by backend
      console.log(`✅ Token acquired for ${options.method || 'GET'} ${endpoint}, length: ${token.length}`);
      authHeaders['Authorization'] = `Bearer ${token}`;
    }

    return authHeaders;
  }

  // ENHANCED 401 HANDLING: Retry with exponential backoff
  // Only retry authentication failures - not for write operations to prevent duplicates
  private async handleUnauthorized<T>(
    endpoint: string,
    options: RequestInit,
    retryAttempt: number,
    maxRetries: number,
    baseDelay: number
  ): Promise<ApiResponse<T> | null> {
    if (retryAttempt < maxRetries) {
      // Wait with exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, retryAttempt);
      await new Promise(resolve => setTimeout(resolve, delay));

      // Try to refresh tokens by calling the getters again
      // This will trigger MSAL to refresh if possible
      try {
        if (this.getAuthToken) {
          await this.getAuthToken();
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }

      // Retry the request
      return this.request<T>(endpoint, options, retryAttempt + 1);
    }
    return null;
  }

  // Helper function to check if endpoint should be logged
  private shouldLogEndpointError(endpoint: string): boolean {
    return !endpoint.includes('drift');
  }

  // Helper function to check if 404 should be logged for specific endpoints  
  private should404BeLogged(endpoint: string): boolean {
    return this.shouldLogEndpointError(endpoint) && !endpoint.includes('notifications');
  }

  // Log specific error types for debugging (but not tokens!)
  private logHttpError(
    status: number,
    endpoint: string,
    token: string | null,
    retryAttempt: number,
    maxRetries: number,
    errorMessage: string
  ): void {
    if (status === 401) {
      if (retryAttempt >= maxRetries) {
        console.error(`🔒 Authentication failed after ${maxRetries} retries`);
      }
      if (!token) {
        console.error('🔒 Request failed: No authentication token available');
      }
    } else if (status === 403) {
      // Only log permission errors for non-drift endpoints
      if (this.shouldLogEndpointError(endpoint)) {
        console.error('🚫 Request failed:', errorMessage);
      }
    } else if (status === 404) {
      // Don't log 404 for drift endpoints - they may not exist yet
      if (this.should404BeLogged(endpoint)) {
        console.warn(`Endpoint not found: ${endpoint}`);
      }
    } else if (status >= 500) {
      // Only log server errors for non-drift endpoints
      if (this.shouldLogEndpointError(endpoint)) {
        console.error('🔥 Server error:', errorMessage);
      }
    }
  }

  // Create appropriate ServiceError based on HTTP status
  private createHttpError(
    status: number,
    endpoint: string,
    errorMessage: string,
    errorData: ApiError | null
  ): ServiceError {
    if (status === 401) {
      return ServiceError.unauthorized(errorMessage, errorData);
    } else if (status === 403) {
      // Use the actual error message from the API instead of a generic one
      return ServiceError.forbidden(errorMessage);
    } else if (status === 404) {
      return ServiceError.notFound(endpoint);
    } else if (status === 429) {
      const retryAfter = errorData?.error?.details?.retryAfter;
      return ServiceError.rateLimited(retryAfter);
    } else if (status >= 500) {
      return ServiceError.serverError(errorMessage);
    }

    // For other status codes, create a general ServiceError
    return new ServiceError(errorMessage, {
      statusCode: status,
      code: errorData?.error?.code || `HTTP_${status}`,
      details: errorData?.error?.details
    });
  }

  private createErrorFromCatch(error: any): ServiceError {
    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      return new ServiceError('Network error: Unable to reach the server', {
        code: 'NETWORK_ERROR',
        isRetryable: true,
        cause: error
      });
    }

    // Handle timeout errors
    if (error instanceof Error && error.message?.includes('timeout')) {
      return new ServiceError('Request timeout: The server took too long to respond', {
        code: 'TIMEOUT',
        statusCode: 408,
        isRetryable: true,
        cause: error
      });
    }

    // For any other error, wrap it in a ServiceError
    return ServiceError.fromHttpError(error);
  }

  // Helper to parse error response from failed HTTP request
  private async parseErrorResponse(response: Response): Promise<{ errorData: ApiError | null; errorMessage: string }> {
    let errorData: ApiError | null = null;
    let errorMessage = `HTTP ${response.status}`;

    try {
      errorData = await response.json();
      errorMessage = errorData?.error?.message || errorMessage;
    } catch {
      // Response wasn't JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }

    return { errorData, errorMessage };
  }

  private async handleFailedResponse<T>(
    response: Response,
    endpoint: string,
    options: RequestInit,
    retryAttempt: number,
    maxRetries: number,
    baseDelay: number,
    authToken: string | null
  ): Promise<ApiResponse<T>> {
    // ONLY retry on 401 Unauthorized (because we refresh tokens)
    if (response.status === 401) {
      const unauthorizedRetry = await this.handleUnauthorized<T>(
        endpoint, options, retryAttempt, maxRetries, baseDelay
      );
      if (unauthorizedRetry) return unauthorizedRetry;
    }

    const { errorData, errorMessage } = await this.parseErrorResponse(response);

    this.logHttpError(
      response.status, endpoint, authToken,
      retryAttempt, maxRetries, errorMessage
    );

    throw this.createHttpError(
      response.status, endpoint, errorMessage, errorData
    );
  }

  private async retryIfPossibleOrThrow<T>(
    error: ServiceError,
    endpoint: string,
    options: RequestInit,
    retryAttempt: number,
    maxRetries: number,
    baseDelay: number,
  ): Promise<ApiResponse<T>> {
    if (error.isRetryable && retryAttempt < maxRetries) {
      const delay = baseDelay * Math.pow(2, retryAttempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      return this.request<T>(endpoint, options, retryAttempt + 1);
    }
    throw error;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryAttempt: number = 0,
  ): Promise<ApiResponse<T>> {
    // Retry all operations (including write operations) on authentication failures
    // The 401 retry logic will refresh tokens and retry, which is safe even for POST/PUT/PATCH/DELETE
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
  
    const normalizedEndpoint = this.normalizeEndpoint(endpoint);
    const url = `${this.baseUrl}${normalizedEndpoint}`;
    const authHeaders = await this.prepareAuthHeaders(endpoint, options);

   const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      } as HeadersInit,
      ...options,
    };
  
    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        return await this.handleFailedResponse<T>(
          response, endpoint, options, retryAttempt, maxRetries, baseDelay,
          authHeaders['Authorization']?.replace('Bearer ', '') || null
        );
      }

      if (response.status === 204) {
        return { success: true, data: undefined as T };
      }

      const data: ApiResponse<T> = await response.json();
      return data;
    } catch (error) {
      // Convert to ServiceError if needed, then retry or throw
      const serviceError = error instanceof ServiceError ? error : this.createErrorFromCatch(error);
      return await this.retryIfPossibleOrThrow<T>(serviceError, endpoint, options, retryAttempt, maxRetries, baseDelay);
    }
  }

  // GET request,
  async get<T>(endpoint: string, params?: Record<string, string>, abortSignal?: AbortSignal): Promise<ApiResponse<T>> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';

    return this.request<T>(`${endpoint}${queryString}`);
  }

  // POST request
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string; environment: string }> {
    const response = await fetch(`${this.baseUrl}/health`);
    return response.json();
  }
}

export const apiClient = new ApiClient();
export type { ApiResponse, ApiError };
export default apiClient;
